import React, { useContext } from "react";
import { Box, Button, Typography } from "@mui/material";
import { VerificationSteps } from "./VerificationSteps";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "AuthContextWrapper"
import { ExecutorVerificationContext } from "ExecutorVerificationContextWrapper";
import { ExecutorVerificationOutcomes } from "./flow";
import { EXECUTOR_VERIFICATION_IN_PROGRESS_ID, EXECUTOR_VERIFICATION_IN_REVIEW_ID, EXECUTOR_VERIFICATION_NOT_STARTED_ID, EXECUTOR_VERIFICATION_REJECTED_ID } from ".";
import { CheckCircleOutline } from "@mui/icons-material";
import { OUTCOMES } from "../../../constants";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { KnowYourExecutorAPI } from "apis/KnowYourExecutorAPI";
import { Loading } from "views/shared/Loading";

export const AboutTheEstateScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { user, flow } = useAuthContext()
  const { steps, setSteps, stepDetailsMap, statusesMap, setVerificationStepId } = useContext(ExecutorVerificationContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)

  const myStepStartIndex = 3
  const myStepEndIndex = 5
  const mySteps = steps?.filter((s, index) => index >= myStepStartIndex && index <= myStepEndIndex) || []

  const handleSubflowStart = async (subFlowTitle: string, newVerificationStepId: number): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    switch (subFlowTitle) {
      case "A copy of the death certificate":
        flow?.navigateToNext(
          currentScreen,
          ExecutorVerificationOutcomes.StartDeathCertificateSubFlow,
          navigate
        );
        break;
      case "A copy of the will":
        flow?.navigateToNext(
          currentScreen,
          ExecutorVerificationOutcomes.StartCopyOfWillSubFlow,
          navigate
        );
        break;
      case "Your court “Letters” or Executor Agreement":
        flow?.navigateToNext(
          currentScreen,
          ExecutorVerificationOutcomes.StartLettersOrAgreementSubFlow,
          navigate
        );
        break;
      default:
        return false;
    }

    setVerificationStepId(newVerificationStepId)

    return true;
  };

  async function handleAllStepsSubmit() {
    if(!mySteps) {
      console.error("no steps to submit")
      return 
    }
    if(!user?.id) {
      console.error("no user id found")
      return
    }
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    setIsSubmitting(true)

    const submittedSuccessfully = await KnowYourExecutorAPI.submitAllForReview(user?.id)

    setIsSubmitting(false)

    if(submittedSuccessfully) {
      setSteps(steps?.map((s,i) => {
        if(i >= myStepStartIndex && i <= myStepEndIndex) {
          s.statusId = EXECUTOR_VERIFICATION_IN_REVIEW_ID
        }
        return s
      }) || [])
    }

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
  }

  function allowFullSubmission():boolean {
    return !mySteps?.find(s => s.statusId === EXECUTOR_VERIFICATION_IN_PROGRESS_ID 
      || s.statusId === EXECUTOR_VERIFICATION_NOT_STARTED_ID
      || s.statusId === EXECUTOR_VERIFICATION_REJECTED_ID
    )
  }

  function allInReview():boolean {
    return !mySteps?.find(s => s.statusId !== EXECUTOR_VERIFICATION_IN_REVIEW_ID)
  }

  if (!currentScreen) {
    return <></>;
  }

  return (
    <FlowScreenWrapper
      title="Almost there! We just need a copy of these legal documents"
      subTitle={[
        "Legal documents like a Will and Probate Court “Letters” serve as definitive proof that ",
        "you are legally qualified to serve as Executor/Administrator and to carry out duties on ",
        "behalf of the estate.",
      ].join(" ")}
    >
      <div>
        <Box sx={{ mb: 2 }}>
          {(!mySteps || mySteps.length===0) && <Loading />}
          {(mySteps && mySteps.length > 0) && <VerificationSteps
            steps={mySteps}
            stepDetailsMap={stepDetailsMap}
            statusesMap={statusesMap}
            onButtonClick={handleSubflowStart}
          />}
        </Box>
        <Box sx={{ mb: 2, textAlign:"center", alignContent:"center",alignItems:"center", clear:"both" }}>
          <span style={{float:"left", marginLeft:"20px"}}>
            <Link to="/flow/executor-verification/about-you">
              <Box sx={{color:"secondary"}}>
                <ArrowBackIcon color={"secondary"} />
              </Box>
            </Link>
          </span>

          <Button variant="contained" 
            color="secondary" 
            disabled={isSubmitting || !mySteps || !allowFullSubmission()}
            style={{minWidth:"200px", marginBottom:"1rem"}}
            onClick={handleAllStepsSubmit}  
          >
            Continue
          </Button>

          {mySteps && mySteps.length > 0 && allInReview() && <Typography variant="body1" component={"p"} style={{marginLeft:"2rem"}}>
            <CheckCircleOutline color="secondary" style={{marginBottom:"-5px", marginRight:"5px"}} />
            Your submissions are under review!
          </Typography>}
        </Box>
      </div>
    </FlowScreenWrapper>
  );
};
